import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/ui-kit";
import { subjectTheme } from "@/lib/subjects";
import { ProgramCourse } from "@/components/course/ProgramCourse";
import { dueGroup, fmtDue } from "@/lib/due";
import { openFile } from "@/components/course/CourseUnits";
import {
  BookOpen, PenLine, FileText, ArrowRight, CheckCircle2, Clock,
  CalendarClock, Calculator, FlaskConical, Trophy, MessageSquareText,
} from "lucide-react";

const SUBJECT_ICON = { writing: PenLine, maths: Calculator, science: FlaskConical, english: BookOpen, general: FileText };

function statusChip(h) {
  if (h.student_status === "completed") {
    if (h.results_released && h.score != null)
      return { label: `Marked · ${Math.round(h.score)}%`, cls: "bg-emerald-50 border-emerald-300 text-emerald-700", dot: "bg-emerald-500" };
    if (h.results_released)
      return { label: "Reviewed", cls: "bg-emerald-50 border-emerald-300 text-emerald-700", dot: "bg-emerald-500" };
    return { label: "Awaiting tutor feedback", cls: "bg-purple-50 border-purple-300 text-purple-700", dot: "bg-purple-500" };
  }
  const g = dueGroup(h.due_date);
  if (g === "past") return { label: "Overdue", cls: "bg-rose-50 border-rose-300 text-rose-700", dot: "bg-rose-500" };
  if (g === "soon") return { label: "Due this week", cls: "bg-rose-50 border-rose-300 text-rose-700", dot: "bg-rose-500" };
  return { label: "Outstanding", cls: "bg-amber-50 border-amber-300 text-amber-800", dot: "bg-amber-500" };
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    api.get("/homework").then((r) => setRows(r.data));
    api.get("/student/dashboard").then((r) => setDash(r.data)).catch(() => setDash({}));
  }, []);
  if (!rows) return <Loading />;

  const isWriting = (h) => h.format === "writing" || h.questions?.[0]?.type === "writing";
  const go = (h) => {
    if (h.student_status === "completed") {
      if (isWriting(h)) return navigate(`/student/writing/${h.id}`);
      if (h.attempt_id) return navigate(`/student/result/${h.attempt_id}`);
    }
    navigate(isWriting(h) ? `/student/writing/${h.id}` : `/student/exam/${h.id}`);
  };

  const notDone = rows.filter((h) => h.student_status !== "completed");
  const dueThisWeek = notDone.filter((h) => ["soon", "past"].includes(dueGroup(h.due_date)));
  const outstanding = notDone.filter((h) => !["soon", "past"].includes(dueGroup(h.due_date)));

  const programName = rows[0]?.program_name || "Your course";

  const TaskRow = ({ h, theme }) => {
    const chip = statusChip(h);
    const Icon = SUBJECT_ICON[theme.key] || FileText;
    const writing = isWriting(h);
    return (
      <button onClick={() => go(h)} data-testid={`timeline-item-${h.id}`}
        className={`group relative flex w-full items-center gap-3 rounded-xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md ${theme.chipHover}`}>
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${theme.soft} ${theme.icon}`}><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-heading font-bold text-slate-900">{h.title}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
            {h.due_date && <span className="font-semibold">{fmtDue(h.due_date)}</span>}
            <span>{writing ? "Written task" : h.format === "saq" ? "Short answers" : h.format === "mixed" ? "MCQ + short answers" : `${h.question_count} questions`}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {h.mode === "timed" ? `${h.time_limit}m` : "Practice"}</span>
          </div>
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${chip.cls}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} /> {chip.label}
        </span>
      </button>
    );
  };

  return (
    <div className="animate-fade-up">
      {/* Course banner */}
      <div data-testid="course-banner" className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-900 via-indigo-800 to-purple-700 p-6 text-white shadow-xl sm:p-8">
        <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 right-20 h-36 w-36 rounded-full bg-indigo-400/20 blur-2xl" />
        <p className="relative text-sm font-semibold text-white/80">Welcome back, {user?.name?.split(" ")[0]}</p>
        <h1 className="relative mt-1 font-heading text-2xl font-extrabold tracking-tight sm:text-3xl lg:text-4xl">{programName}</h1>
        <div className="relative mt-4 flex flex-wrap gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 font-semibold backdrop-blur"><CalendarClock className="h-4 w-4" /> {dueThisWeek.length} due this week</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 font-semibold backdrop-blur"><CheckCircle2 className="h-4 w-4" /> {rows.filter((h) => h.student_status === "completed").length} completed</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 font-semibold backdrop-blur"><Clock className="h-4 w-4" /> {notDone.length} to do</span>
        </div>
      </div>

      {/* Due this week */}
      <section className="mt-8" data-testid="due-this-week-section">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><span className="h-2 w-2 rounded-full bg-rose-500" /> Due this week</h2>
        {dueThisWeek.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">You're all caught up for this week. 🎉</div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {dueThisWeek.map((h) => <TaskRow key={h.id} h={h} theme={subjectTheme(h.section, h.section_name)} />)}
          </div>
        )}
      </section>

      {/* Outstanding */}
      {outstanding.length > 0 && (
        <section className="mt-8" data-testid="outstanding-tasks-section">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><span className="h-2 w-2 rounded-full bg-amber-500" /> Outstanding tasks <span className="text-sm font-semibold text-amber-700">({outstanding.length})</span></h2>
          <div className="grid gap-3 md:grid-cols-2">
            {outstanding.map((h) => <TaskRow key={h.id} h={h} theme={subjectTheme(h.section, h.section_name)} />)}
          </div>
        </section>
      )}

      {/* Program course */}
      <h2 className="mb-4 mt-10 font-heading text-xl font-bold text-slate-900">Your course</h2>
      <ProgramCourse defaultOpen onOpen={(it) => (it.kind !== "homework" ? openFile(it) : navigate(it.is_writing ? `/student/writing/${it.homework_id}` : it.done ? "/student/history" : `/student/exam/${it.homework_id}`))} />

      {/* Recent results */}
      {dash?.recent?.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-bold text-slate-900"><Trophy className="h-5 w-5 text-amber-500" /> Recent results</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {dash.recent.map((r) => (
              <Link key={r.attempt_id} to={`/student/result/${r.attempt_id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm hover:shadow-md">
                <span className="text-sm font-semibold text-slate-900">{r.title || r.section_name}</span>
                <span className="font-heading font-extrabold text-indigo-700">{Math.round(r.percentage)}%</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Link to="/student/progress" className="mt-8 inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:underline">
        <MessageSquareText className="h-4 w-4" /> View full progress <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
